import { NetworkError, ProblemError, type HttpClient, type Id } from '@/model';
import { object } from './decode';
import { AccountStore, advanceLocalRead, acceptSeen, queueReadMark } from './store';

interface Pending { max: number; timer?: ReturnType<typeof setTimeout>; flight?: Promise<void>; waiters: { seq: number; resolve(): void; reject(error: unknown): void }[] }
/** Durable, monotonic read marks. The transcript owns contiguous visibility evidence (API §13.4). */
export class ReadMarkers {
  private running = false;
  private paused = false;
  private readonly pending = new Map<Id, Pending>();
  constructor(private readonly http: HttpClient, private readonly store: () => AccountStore | undefined) {}
  start(): void { this.running = true; globalThis.addEventListener?.('online', this.wake); this.wake(); }
  stop(): void { this.running = false; globalThis.removeEventListener?.('online', this.wake); for (const p of this.pending.values()) { if (p.timer) clearTimeout(p.timer); p.waiters.splice(0).forEach(w => w.resolve()); } this.pending.clear(); }
  setPaused(paused: boolean): void { this.paused = paused; if (!paused) this.wake(); }
  wake = (): void => {
    const db = this.store(); if (!db || !this.running || this.paused) return;
    void db.readMarks.toArray().then(rows => { if (db !== this.store() || !this.running) return; for (const row of rows) { const p = this.value(row.conversation_id, row.seq); this.schedule(row.conversation_id, p, 0); } }).catch(() => undefined);
  };
  private value(id: Id, seq: number): Pending { let p = this.pending.get(id); if (!p) { p = { max: seq, waiters: [] }; this.pending.set(id, p); } p.max = Math.max(p.max, seq); return p; }
  private schedule(id: Id, p: Pending, delay: number) { if (!this.running || this.paused || p.timer || p.flight) return; p.timer = setTimeout(() => { p.timer = undefined; void this.flush(id, p); }, delay); }
  async markRead(id: Id, seq: number): Promise<void> {
    if (!Number.isSafeInteger(seq) || seq < 0) return;
    const db = this.store(); if (!db) throw new ProblemError({ status: 401, code: 'unauthorized' });
    let target = 0;
    await db.transaction('rw', db.tables, async () => {
      target = await queueReadMark(db, id, seq);
    });
    if (!target || this.store() !== db) return;
    const p = this.value(id, target);
    const accepted = new Promise<void>((resolve, reject) => { p.waiters.push({ seq: target, resolve, reject }); });
    this.schedule(id, p, 300); return accepted;
  }
  async reportSeen(id: Id, seqs: readonly number[]): Promise<void> {
    const db = this.store(); if (!db || this.paused) return;
    const target = await acceptSeen(db, id, seqs);
    if (target && db === this.store()) this.schedule(id, this.value(id, target), 300);
  }
  async flushSeen(id: Id): Promise<void> {
    const db = this.store(); if (!db || !this.running || this.paused) return;
    const row = await db.readMarks.get(id); if (!row || db !== this.store()) return;
    const p = this.value(id, row.seq); if (p.timer) clearTimeout(p.timer); p.timer = undefined;
    if (p.flight) await p.flight; else await this.flush(id, p);
  }
  private async flush(id: Id, p: Pending): Promise<void> {
    const db = this.store(); if (!db || !this.running || this.paused || p.flight) return;
    p.flight = (async () => {
      const row = await db.readMarks.get(id);
      if (!row) { p.waiters.splice(0).forEach(w => w.resolve()); return; }
      if (!this.running || this.paused || db !== this.store()) return;
      const target = row.seq; const generation = (await db.meta.get('sync'))?.generation;
      try {
        const response = object(await this.http.post(`/v1/conversations/${id}/read`, { seq: target }));
        if (!this.running || db !== this.store()) return;
        await db.transaction('rw', db.tables, async () => {
          if (this.paused || (await db.meta.get('sync'))?.generation !== generation) return;
          const held = await db.readMarks.get(id);
          if (held && held.seq <= target) await db.readMarks.delete(id);
          if ((await db.meta.get('sync'))?.generation === generation) await advanceLocalRead(db, id, typeof response.read_seq === 'number' ? response.read_seq : target);
        });
        const done = p.waiters.filter(w => w.seq <= target); p.waiters = p.waiters.filter(w => w.seq > target); done.forEach(w => w.resolve());
      } catch (error) {
        if (!this.running || db !== this.store()) return;
        if (!await db.readMarks.get(id)) { p.waiters.splice(0).forEach(w => w.resolve()); return; }
        const transient = error instanceof NetworkError || !(error instanceof ProblemError) || error.status === 429 || error.status >= 500;
        if (!transient) { await db.readMarks.delete(id); p.waiters.splice(0).forEach(w => w.reject(error)); return; }
        const delay = Math.max(error instanceof ProblemError ? (error.retryAfter ?? 0) * 1000 : 0, Math.min(30000, 1000 * 2 ** Math.min(row.attempts, 5)));
        await db.readMarks.update(id, { attempts: row.attempts + 1, next_attempt_at: Date.now() + delay });
      }
    })().catch(() => undefined).finally(async () => {
      p.flight = undefined;
      if (!this.running || db !== this.store()) return;
      try { const row = await db.readMarks.get(id); if (row) this.schedule(id, p, Math.max(0, row.next_attempt_at - Date.now())); } catch { /* A stopped/deleted store has no more work. */ }
    });
    await p.flight;
  }
}
